import { setTraits, ChildOf, Pair, Transform, defineRelation, type Plugin, type State, type System } from '@multiplekex/shallot';
import { Text, Line, Arrow } from '@multiplekex/shallot/extras';
import { LINE, VALID, INVALID } from './colors';

export const TokenReplacement = {
  trigger: [] as number[],
};

setTraits(TokenReplacement, {
  defaults: () => ({ trigger: 0 }),
});

const tokenReplacementTexts = new Map<number, { original: string; replacement: string }>();

export function setTokenReplacementTexts(eid: number, original: string, replacement: string): void {
  tokenReplacementTexts.set(eid, { original, replacement });
}

export { Border, BorderPlugin } from './border';
export { ProbabilityDistribution, DistributionBar, BarHighlight, ProbabilityDistributionPlugin, setDistributionProbabilities } from './probability-distribution';
export { GradientLegend, GradientLegendPlugin } from './gradient-legend';

export const Network = {
  active: [] as number[],
  lineTransition: [] as number[],
};

setTraits(Network, {
  defaults: () => ({ active: 0, lineTransition: 1 }),
});

export const TargetModel = {};
export const DraftModel = {};
export const ValidationModel = {};

export const Slot = {
  index: [] as number[],
  transition: [] as number[],
};

setTraits(Slot, {
  defaults: () => ({ index: 0, transition: 1 }),
});

export const Latent = {};
export const Output = {};

export const ValidationMark = {
  valid: [] as number[],
  transition: [] as number[],
};

setTraits(ValidationMark, {
  defaults: () => ({ valid: 1, transition: 0 }),
});

const SLOT_OFFSET_Z = -4;
const CHAR_WIDTH_RATIO = 0.55;
const WORD_GAP = 0.7;
const OUTPUT_GAP = 1.4;
const LINE_THICKNESS = 2;

function getSlotTextWidth(state: State, slotEid: number): number {
  for (const textEid of state.query([Pair(ChildOf.relation, slotEid), Text])) {
    const content = Text.content[textEid] ?? '';
    const fontSize = Text.fontSize[textEid] || 1;
    return content.length * fontSize * CHAR_WIDTH_RATIO;
  }
  return 1;
}

const SlotTransitionSystem: System = {
  group: 'simulation',
  update(state: State) {
    for (const slotEid of state.query([Slot, Transform])) {
      const t = Slot.transition[slotEid];
      Transform.scaleX[slotEid] = t;
      Transform.scaleY[slotEid] = t;
      Transform.scaleZ[slotEid] = t;
    }
  },
};

const SlotArrangementSystem: System = {
  group: 'simulation',
  update(state: State) {
    for (const networkEid of state.query([Network, Transform])) {
      const allSlots = [...state.query([Pair(ChildOf.relation, networkEid), Slot, Transform])];
      if (allSlots.length === 0) continue;

      allSlots.sort((a, b) => Slot.index[a] - Slot.index[b]);

      const inputs = allSlots.filter((eid) => !state.hasComponent(eid, Output));
      const outputs = allSlots.filter((eid) => state.hasComponent(eid, Output));

      const inputWidths = inputs.map((eid) => getSlotTextWidth(state, eid));
      const inputTotalWidth = inputWidths.reduce((sum, w) => sum + w, 0) + WORD_GAP * Math.max(0, inputs.length - 1);

      let x = -inputTotalWidth / 2;
      for (let i = 0; i < inputs.length; i++) {
        const slotEid = inputs[i];
        const w = inputWidths[i];
        Transform.posX[slotEid] = x + w / 2;
        Transform.posY[slotEid] = 0;
        Transform.posZ[slotEid] = SLOT_OFFSET_Z;
        x += w + WORD_GAP;
      }

      x = inputTotalWidth / 2 + OUTPUT_GAP;
      for (let i = 0; i < outputs.length; i++) {
        const slotEid = outputs[i];
        const w = getSlotTextWidth(state, slotEid);
        Transform.posX[slotEid] = x + w / 2;
        Transform.posY[slotEid] = 0;
        Transform.posZ[slotEid] = SLOT_OFFSET_Z;
        x += w + WORD_GAP;
      }
    }
  },
};

const NetworkLine = {};
const NetworkLineTarget = defineRelation('network-line-target', { exclusive: true });

const NetworkLinesSystem: System = {
  group: 'simulation',
  update(state: State) {
    for (const networkEid of state.query([Network, Transform])) {
      const lineTransition = Network.lineTransition[networkEid];

      for (const slotEid of state.query([Pair(ChildOf.relation, networkEid), Slot, Transform])) {
        const existingLines = [...state.query([Pair(NetworkLineTarget.relation, slotEid), NetworkLine, Line])];

        let lineEid: number;
        if (existingLines.length === 0) {
          lineEid = state.addEntity();
          state.addComponent(lineEid, Transform);
          state.addComponent(lineEid, Line);
          state.addComponent(lineEid, Arrow);
          state.addComponent(lineEid, NetworkLine);
          state.addComponent(lineEid, Pair(ChildOf.relation, networkEid));
          state.addComponent(lineEid, Pair(NetworkLineTarget.relation, slotEid));

          Line.color[lineEid] = LINE;
          Line.thickness[lineEid] = LINE_THICKNESS;
          Line.visible[lineEid] = 1;

          Arrow.start[lineEid] = 0;
          Arrow.end[lineEid] = 1;
          Arrow.size[lineEid] = 1;
        } else {
          lineEid = existingLines[0];
        }

        const slotX = Transform.posX[slotEid];
        const slotY = Transform.posY[slotEid];
        const slotZ = Transform.posZ[slotEid];
        const t = Math.min(1, Slot.transition[slotEid]);

        Line.offsetX[lineEid] = slotX * t;
        Line.offsetY[lineEid] = slotY * t;
        Line.offsetZ[lineEid] = slotZ * t;
        Line.opacity[lineEid] = lineTransition;
      }
    }
  },
};

const LatentArrow = {};
const LatentArrowOwner = defineRelation('latent-arrow-owner', { exclusive: true });

const ValidationLine = {};
const ValidationLineTarget = defineRelation('validation-line-target', { exclusive: true });

const LatentArrowsSystem: System = {
  group: 'simulation',
  update(state: State) {
    for (const slotEid of state.query([Slot, Transform])) {
      const latents = [...state.query([Pair(ChildOf.relation, slotEid), Latent, Transform])];
      if (latents.length === 0) continue;

      const latentEid = latents[0];

      const existingArrows = [...state.query([Pair(LatentArrowOwner.relation, slotEid), LatentArrow, Line])];

      let arrowEid: number;
      if (existingArrows.length === 0) {
        arrowEid = state.addEntity();
        state.addComponent(arrowEid, Transform);
        state.addComponent(arrowEid, Line);
        state.addComponent(arrowEid, Arrow);
        state.addComponent(arrowEid, LatentArrow);
        state.addComponent(arrowEid, Pair(ChildOf.relation, slotEid));
        state.addComponent(arrowEid, Pair(LatentArrowOwner.relation, slotEid));

        Line.color[arrowEid] = LINE;
        Line.thickness[arrowEid] = LINE_THICKNESS;
        Line.visible[arrowEid] = 1;
        Line.opacity[arrowEid] = 1;

        Arrow.start[arrowEid] = 0;
        Arrow.end[arrowEid] = 1;
        Arrow.size[arrowEid] = 1;
      } else {
        arrowEid = existingArrows[0];
      }

      const latentZ = Transform.posZ[latentEid] - 3.5;

      Transform.posX[arrowEid] = 0;
      Transform.posY[arrowEid] = 0;
      Transform.posZ[arrowEid] = latentZ;

      Line.offsetX[arrowEid] = 0;
      Line.offsetY[arrowEid] = 0;
      Line.offsetZ[arrowEid] = -0.8;
    }
  },
};

const ValidationLinesSystem: System = {
  group: 'simulation',
  update(state: State) {
    for (const validationEid of state.query([ValidationModel, Transform])) {
      const validationScale = Transform.scaleX[validationEid];
      if (validationScale < 0.01) continue;

      for (const draftEid of state.query([DraftModel, Transform])) {
        const draftSlots = [...state.query([Pair(ChildOf.relation, draftEid), Slot, Transform])];
        if (draftSlots.length === 0) continue;

        const draftX = Transform.posX[draftEid];
        const draftY = Transform.posY[draftEid];
        const draftZ = Transform.posZ[draftEid];

        for (const slotEid of draftSlots) {
          const slotTransition = Math.max(0, Math.min(1, Slot.transition[slotEid]));

          const existingLines = [...state.query([Pair(ValidationLineTarget.relation, slotEid), ValidationLine, Line])];

          let lineEid: number;
          if (existingLines.length === 0) {
            lineEid = state.addEntity();
            state.addComponent(lineEid, Transform);
            state.addComponent(lineEid, Line);
            state.addComponent(lineEid, Arrow);
            state.addComponent(lineEid, ValidationLine);
            state.addComponent(lineEid, Pair(ValidationLineTarget.relation, slotEid));

            Line.color[lineEid] = LINE;
            Line.thickness[lineEid] = LINE_THICKNESS;
            Line.visible[lineEid] = 1;

            Arrow.start[lineEid] = 0;
            Arrow.end[lineEid] = 1;
            Arrow.size[lineEid] = 1;
          } else {
            lineEid = existingLines[0];
          }

          if (slotTransition < 0.01) {
            Line.opacity[lineEid] = 0;
            continue;
          }

          const slotX = Transform.posX[slotEid];
          const slotZ = Transform.posZ[slotEid];
          const worldSlotX = draftX + slotX * slotTransition;
          const worldSlotY = draftY;
          const worldSlotZ = draftZ + slotZ * slotTransition;

          const validationX = Transform.posX[validationEid];
          const validationY = Transform.posY[validationEid];
          const validationZ = Transform.posZ[validationEid];

          Transform.posX[lineEid] = validationX;
          Transform.posY[lineEid] = validationY;
          Transform.posZ[lineEid] = validationZ;

          Line.offsetX[lineEid] = (worldSlotX - validationX) * validationScale;
          Line.offsetY[lineEid] = (worldSlotY - validationY) * validationScale;
          Line.offsetZ[lineEid] = (worldSlotZ - validationZ) * validationScale;
          Line.opacity[lineEid] = validationScale * slotTransition;
        }
      }
    }
  },
};

const TokenReplacementSystem: System = {
  group: 'simulation',
  update(state: State) {
    for (const eid of state.query([TokenReplacement, Text])) {
      const trigger = TokenReplacement.trigger[eid];
      const texts = tokenReplacementTexts.get(eid);
      if (!texts) continue;

      const currentText = Text.content[eid] ?? '';
      const { original, replacement } = texts;

      if (trigger > 0.5 && currentText !== replacement && replacement) {
        Text.content[eid] = replacement;
      } else if (trigger <= 0.5 && currentText !== original && original) {
        Text.content[eid] = original;
      }
    }
  },
};

export const SpecdecPlugin: Plugin = {
  components: { Network, TargetModel, DraftModel, ValidationModel, Slot, Latent, Output, NetworkLine, LatentArrow, ValidationLine, TokenReplacement },
  systems: [SlotTransitionSystem, SlotArrangementSystem, NetworkLinesSystem, LatentArrowsSystem, ValidationLinesSystem, TokenReplacementSystem],
};
